import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatNumber } from '../lib/format.js';

/**
 * Inline SVG charts for the dashboards. Deliberately small: one hue per chart,
 * hairline grid, thin marks, a hover tooltip on every mark, and a table view for
 * anybody who cannot use the picture.
 *
 * The single green hue is the sequential ramp for magnitude. Steps were validated
 * against the white chart surface (ordinal light end 2.54:1, monotone lightness,
 * hue spread 3 degrees). If a dark mode is ever added, the matching validated dark
 * steps are #7fd0aa / #3aa877 / #1c6b4c against a #14161a surface.
 */

const SERIES = '#1f8b5e';
const SERIES_SOFT = '#5cb389';
const GRID = '#e8e8e2';
const AXIS = '#c8c8c0';
const INK_MUTED = '#6b6a65';

/**
 * Builds an axis from the largest value in the series: a whole-number step a
 * reader can add up in their head, evenly spaced, with a little headroom above the
 * peak so the line never runs along the top edge. Picking the step first is what
 * keeps the gridlines and their labels in step with each other.
 */
const niceScale = (peak) => {
  const target = Math.max(Math.ceil(peak), 1);

  for (let magnitude = 1; magnitude <= 1e9; magnitude *= 10) {
    for (const unit of [1, 2, 5]) {
      const step = unit * magnitude;
      let count = Math.ceil(target / step);
      if (count > 4) continue;
      // Headroom: if the peak lands exactly on the top gridline, add one more.
      if (step * count === target) count += 1;
      return { max: step * count, step };
    }
  }

  return { max: target, step: target };
};

const axisTicks = ({ max, step }) => {
  const ticks = [];
  for (let value = 0; value <= max; value += step) ticks.push({ value, fraction: value / max });
  return ticks;
};

/**
 * Measures the container so the SVG can be drawn at true pixel size. Scaling a
 * fixed viewBox down to fit a narrow card shrinks the axis text with it, which
 * left the labels illegible in a three-across layout.
 */
const useMeasuredWidth = (fallback = 640) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0].contentRect.width);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};

const shortDate = (day) => {
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const StatTile = ({ value, label, note, tone }) => (
  <div className={`stat-tile ${tone ? `tone-${tone}` : ''}`}>
    <span className="stat-value">{typeof value === 'number' ? formatNumber(value) : value}</span>
    <span className="stat-label">{label}</span>
    {note ? <span className="stat-note">{note}</span> : null}
  </div>
);

const TableView = ({ caption, rows, valueHeader = 'Count', keyHeader = 'Item' }) => (
  <table className="chart-table">
    <caption className="sr-only">{caption}</caption>
    <thead>
      <tr>
        <th scope="col">{keyHeader}</th>
        <th scope="col">{valueHeader}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.key}>
          <th scope="row">{row.label}</th>
          <td>{formatNumber(row.value)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/**
 * Daily counts over a window, drawn as an area with a line on top. One series, so
 * no legend: the title names it. The peak day is labelled directly and everything
 * else is reachable by hover or in the table.
 */
export const TrendChart = ({ title, subtitle, data = [], accessibleLabel }) => {
  const clipId = useId();
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState(null);
  const [plotRef, measured] = useMeasuredWidth();

  // Drawn 1:1 with the container, so 11px axis text really is 11px.
  const width = Math.max(measured, 240);
  const height = 200;
  const pad = { top: 20, right: 16, bottom: 30, left: 40 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;

  const points = useMemo(() => {
    if (data.length === 0) return [];
    const scale = niceScale(Math.max(...data.map((row) => row.count)));
    const stepX = data.length > 1 ? plotWidth / (data.length - 1) : 0;

    return data.map((row, index) => ({
      ...row,
      x: pad.left + index * stepX,
      y: pad.top + plotHeight - (row.count / scale.max) * plotHeight,
      scale
    }));
  }, [data, plotWidth, plotHeight, pad.left, pad.top]);

  if (data.length === 0) {
    return (
      <figure className="chart-card">
        <figcaption>
          <strong>{title}</strong>
          {subtitle ? <span>{subtitle}</span> : null}
        </figcaption>
        <p className="chart-empty">Nothing recorded in this period yet.</p>
      </figure>
    );
  }

  const ticks = axisTicks(points[0].scale).map((tick) => ({
    value: tick.value,
    y: pad.top + plotHeight - tick.fraction * plotHeight
  }));

  const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x} ${point.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x} ${pad.top + plotHeight} L${points[0].x} ${
    pad.top + plotHeight
  } Z`;

  const peak = points.reduce((best, point) => (point.count > best.count ? point : best), points[0]);
  const last = points[points.length - 1];
  const total = data.reduce((sum, row) => sum + row.count, 0);

  // Nearest point to the pointer, so the hit area is a full column rather than the
  // line itself.
  const onMove = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    const relative = ((event.clientX - box.left) / box.width) * width;
    let nearest = points[0];
    for (const point of points) {
      if (Math.abs(point.x - relative) < Math.abs(nearest.x - relative)) nearest = point;
    }
    setHover(nearest);
  };

  return (
    <figure className="chart-card">
      <figcaption>
        <strong>{title}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
        <button type="button" className="link-btn chart-toggle" onClick={() => setShowTable((value) => !value)}>
          {showTable ? 'Show chart' : 'Show numbers'}
        </button>
      </figcaption>

      {showTable ? (
        <TableView
          caption={accessibleLabel || title}
          keyHeader="Day"
          rows={data.map((row) => ({ key: row.day, label: shortDate(row.day), value: row.count }))}
        />
      ) : (
        <div className="chart-plot" ref={plotRef}>
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`${accessibleLabel || title}. ${formatNumber(total)} in total, peaking at ${formatNumber(
              peak.count
            )} on ${shortDate(peak.day)}.`}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            <defs>
              <linearGradient id={`fade-${clipId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES} stopOpacity="0.22" />
                <stop offset="100%" stopColor={SERIES} stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {ticks.map((tick) => (
              <g key={tick.y}>
                <line x1={pad.left} y1={tick.y} x2={width - pad.right} y2={tick.y} stroke={GRID} strokeWidth="1" />
                <text x={pad.left - 8} y={tick.y + 4} textAnchor="end" className="axis-text">
                  {formatNumber(tick.value)}
                </text>
              </g>
            ))}

            <path d={areaPath} fill={`url(#fade-${clipId})`} />
            <path d={linePath} fill="none" stroke={SERIES} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

            <line
              x1={pad.left}
              y1={pad.top + plotHeight}
              x2={width - pad.right}
              y2={pad.top + plotHeight}
              stroke={AXIS}
              strokeWidth="1"
            />

            {[points[0], points[Math.floor(points.length / 2)], last]
              .filter((point, index, all) => all.indexOf(point) === index)
              .map((point, index, all) => (
                <text
                  key={point.day}
                  x={point.x}
                  y={height - 10}
                  textAnchor={index === 0 ? 'start' : index === all.length - 1 ? 'end' : 'middle'}
                  className="axis-text"
                >
                  {shortDate(point.day)}
                </text>
              ))}

            {hover ? (
              <line x1={hover.x} y1={pad.top} x2={hover.x} y2={pad.top + plotHeight} stroke={AXIS} strokeWidth="1" />
            ) : null}

            {/* The peak carries a direct label; the rest live in the tooltip and table. */}
            <circle cx={peak.x} cy={peak.y} r="4.5" fill={SERIES} stroke="#ffffff" strokeWidth="2" />
            <text
              x={Math.min(Math.max(peak.x, pad.left + 24), width - pad.right - 24)}
              y={Math.max(peak.y - 12, pad.top + 10)}
              textAnchor="middle"
              className="peak-label"
            >
              {formatNumber(peak.count)}
            </text>

            {hover && hover !== peak ? (
              <circle cx={hover.x} cy={hover.y} r="4.5" fill={SERIES} stroke="#ffffff" strokeWidth="2" />
            ) : null}
          </svg>

          {hover ? (
            <div
              className="chart-tooltip"
              style={{ left: `${(hover.x / width) * 100}%` }}
              role="presentation"
            >
              <strong>{formatNumber(hover.count)}</strong>
              <span>{shortDate(hover.day)}</span>
            </div>
          ) : null}
        </div>
      )}

      <p className="chart-footnote">{formatNumber(total)} in total over the period.</p>
    </figure>
  );
};

/**
 * Horizontal bars for ranked magnitudes: towns by listing count, applications by
 * stage. One colour for every bar, because bar length already carries the value.
 * Values sit outside the bar end so nothing is ever clipped.
 */
export const BarList = ({ title, subtitle, rows = [], valueFormatter = formatNumber, keyHeader = 'Item', accessibleLabel }) => {
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState(null);

  if (rows.length === 0) {
    return (
      <figure className="chart-card">
        <figcaption>
          <strong>{title}</strong>
          {subtitle ? <span>{subtitle}</span> : null}
        </figcaption>
        <p className="chart-empty">No data yet.</p>
      </figure>
    );
  }

  const max = Math.max(...rows.map((row) => row.value), 1);

  return (
    <figure className="chart-card">
      <figcaption>
        <strong>{title}</strong>
        {subtitle ? <span>{subtitle}</span> : null}
        <button type="button" className="link-btn chart-toggle" onClick={() => setShowTable((value) => !value)}>
          {showTable ? 'Show chart' : 'Show numbers'}
        </button>
      </figcaption>

      {showTable ? (
        <TableView caption={accessibleLabel || title} keyHeader={keyHeader} rows={rows} />
      ) : (
        <ul className="bar-list" aria-label={accessibleLabel || title}>
          {rows.map((row) => (
            <li
              key={row.key}
              onMouseEnter={() => setHover(row.key)}
              onMouseLeave={() => setHover(null)}
              className={hover === row.key ? 'is-hover' : ''}
            >
              <span className="bar-list-label">{row.label}</span>
              <span className="bar-list-track">
                <span
                  className="bar-list-fill"
                  style={{
                    width: `${Math.max((row.value / max) * 100, row.value > 0 ? 2 : 0)}%`,
                    background: hover === row.key ? SERIES : SERIES_SOFT
                  }}
                />
              </span>
              <span className="bar-list-value">{valueFormatter(row.value)}</span>
              {row.note ? <span className="bar-list-note">{row.note}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
};

export const chartInk = { SERIES, SERIES_SOFT, GRID, AXIS, INK_MUTED };
